/**
 * AssetDisposalWizard — Veräußerung / Entnahme einer Anlage
 *
 * Berechnet Restbuchwert + Veräußerungsgewinn/-verlust live.
 */
import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRightLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/utils/api";

export default function AssetDisposalWizard({ open, onClose, asset, onDisposed }) {
  const [form, setForm] = useState({
    disposal_date: new Date().toISOString().slice(0, 10),
    disposal_type: "sale",
    disposal_proceeds_gross: 0,
    vat_rate: 19,
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      disposal_date: new Date().toISOString().slice(0, 10),
      disposal_type: "sale",
      disposal_proceeds_gross: 0,
      vat_rate: asset?.vat_rate || 19,
      notes: "",
    });
  }, [open, asset]);

  const handleDispose = async () => {
    setSaving(true);
    try {
      const res = await api.post(`/fixed-assets/${asset.id}/dispose`, form);
      const r = res.data;
      const sign = r.gain_loss >= 0 ? "Gewinn" : "Verlust";
      toast.success(
        `Anlage veräußert · Final-AfA ${r.final_afa.toFixed(2)} € · Buchwert ${r.book_value_at_disposal.toFixed(2)} € · ${sign} ${Math.abs(r.gain_loss).toFixed(2)} €`
      );
      onDisposed?.(r);
      onClose?.();
    } catch (err) {
      toast.error(`Veräußerung fehlgeschlagen: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (!asset) return null;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent className="w-full sm:max-w-[520px] overflow-y-auto" data-testid="asset-disposal-wizard">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 text-amber-600" />
            Anlage veräußern / entnehmen
          </SheetTitle>
          <SheetDescription>
            {asset.name} · AK {asset.cost_net?.toFixed(2)} €
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 pt-4 pb-8">
          <div>
            <Label>Datum der Veräußerung</Label>
            <Input type="date" value={form.disposal_date}
                   onChange={(e) => setForm({ ...form, disposal_date: e.target.value })}
                   data-testid="disposal-date" />
          </div>

          <div>
            <Label>Art</Label>
            <Select value={form.disposal_type} onValueChange={(v) => setForm({ ...form, disposal_type: v })}>
              <SelectTrigger data-testid="disposal-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sale">Verkauf</SelectItem>
                <SelectItem value="withdrawal">Entnahme ins Privatvermögen</SelectItem>
                <SelectItem value="theft">Diebstahl / Verlust</SelectItem>
                <SelectItem value="scrap">Verschrottung</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.disposal_type === "sale" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Verkaufserlös (Brutto)</Label>
                <Input type="number" step="0.01" value={form.disposal_proceeds_gross}
                       onChange={(e) => setForm({ ...form, disposal_proceeds_gross: parseFloat(e.target.value) || 0 })}
                       data-testid="disposal-proceeds" />
              </div>
              <div>
                <Label>USt-Satz</Label>
                <Select value={String(form.vat_rate)} onValueChange={(v) => setForm({ ...form, vat_rate: parseFloat(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0 %</SelectItem>
                    <SelectItem value="7">7 %</SelectItem>
                    <SelectItem value="19">19 %</SelectItem>
                    <SelectItem value="20">20 %</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div>
            <Label>Notiz (optional)</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600">
            Das System berechnet bei Bestätigung die anteilige Final-AfA bis zum Veräußerungs-Datum,
            ermittelt den Restbuchwert und bucht ggf. den Veräußerungsgewinn/-verlust.
          </div>

          <div className="flex gap-2 pt-2">
            <Button className="flex-1 bg-amber-600 hover:bg-amber-700"
                    onClick={handleDispose} disabled={saving}
                    data-testid="confirm-dispose-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRightLeft className="h-4 w-4 mr-2" />}
              Veräußerung bestätigen
            </Button>
            <Button variant="outline" onClick={onClose} disabled={saving}>Abbrechen</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
